import { camelCase, mapKeys } from 'lodash';

export interface StyleConfigMap {
    liveboard_config?: StyleTypeConfig;
    group_config?: StyleTypeConfig;
    tile_config?: StyleTypeConfig;
}

export interface StyleTypeConfig {
    [styleType: string]: StyleConfig;
}

export interface StyleConfig {
    [styleKey: string]: ToolbarStyleConfig;
}

export interface ToolbarStyleConfig {
    pure_style_mapping?: Record<string, string>;
    config_style_mapping?: Record<string, string>;
    composite_style_mapping?: Record<string, string>;
}

export interface StyleResult {
    pureStyle: Record<string, string>;
    configStyle: Record<string, string>;
}

/**
 * Converts a snake case object to a camel case object
 * @param source - The source object to convert
 * @returns The converted object
 */
const convertToCamelCase = (source?: Record<string, string>) => {
    if (!source) return {};
    return mapKeys(source, (v, k) => camelCase(k));
};

/**
 * Resolves composite style mappings and returns calculated pure and config style mappings
 * @param config - The toolbar style configuration object
 * @param toolbarConfig - The complete toolbar configuration for resolving composite references
 * @returns Object containing resolved pure_style_mapping and config_style_mapping
 */
export const resolveCompositeStyleMappings = (
    config: ToolbarStyleConfig,
    toolbarConfig: Record<string, any>,
): ToolbarStyleConfig => {
    if (!config) {
        return {
            pure_style_mapping: {},
            config_style_mapping: {},
            composite_style_mapping: {},
        };
    }

    const pure_style_mapping = convertToCamelCase(config.pure_style_mapping);
    const config_style_mapping = convertToCamelCase(
        config.config_style_mapping,
    );
    const composite_style_mapping = convertToCamelCase(
        config.composite_style_mapping,
    );

    const resolve = (compositeMapping: Record<string, string>) => {
        Object.entries(compositeMapping).forEach(([key, value]) => {
            const nested = toolbarConfig[key]?.[value];
            if (!nested) return;

            Object.assign(
                pure_style_mapping,
                convertToCamelCase(nested.pure_style_mapping) || {},
            );
            Object.assign(
                config_style_mapping,
                convertToCamelCase(nested.config_style_mapping) || {},
            );
            Object.assign(
                composite_style_mapping,
                convertToCamelCase(nested.composite_style_mapping) || {},
            );

            if (nested.composite_style_mapping) {
                resolve(nested.composite_style_mapping);
            }
        });
    };

    resolve(config.composite_style_mapping || {});

    return {
        pure_style_mapping,
        config_style_mapping,
        composite_style_mapping,
    };
};

/**
 * Transforms the style config map to a camel case format and calculates the composite style mappings
 * @param styleConfigMap - The style config map to transform
 * @returns The transformed style config map
 */
export const transformStyleConfigMap = (
    styleConfigMap: StyleConfigMap,
): StyleConfigMap => {
    // Transform each config type
    const transformedConfig: Record<string, StyleTypeConfig> = {};
    Object.entries(styleConfigMap).forEach(([configType, styleConfigs]) => {
        if (typeof styleConfigs === 'object' && styleConfigs !== null) {
            transformedConfig[configType] = {};

            // styleConfigs is of the form:
            // {
            //     lb_density_type: {}
            //     lb_border_type: {}
            // }
            Object.entries(styleConfigs).forEach(([styleKey, styleConfig]) => {
                if (typeof styleConfig === 'object' && styleConfig !== null) {
                    const camelCaseStyleKey = camelCase(styleKey);
                    transformedConfig[configType][camelCaseStyleKey] = {};

                    // styleConfig is of the form:
                    // {
                    //     LOOSE: {}
                    //     REGULAR: {}
                    //     TIGHT: {}
                    // }
                    Object.entries(styleConfig).forEach(
                        ([variantKey, variantConfig]) => {
                            transformedConfig[configType][camelCaseStyleKey][
                                variantKey
                            ] = resolveCompositeStyleMappings(
                                variantConfig,
                                styleConfigs,
                            );
                        },
                    );
                }
            });
        }
    });

    return transformedConfig;
};

/**
 * Processes toolbar configuration and extracts pure and config styles
 * @param toolbarConfig - The toolbar configuration object
 * @param compositeStyle - The active composite style configuration
 * @returns Object containing processed pure and config styles
 */
export const processToolbarConfig = (
    toolbarConfig: StyleTypeConfig,
    compositeStyle: Partial<Record<string, string>>,
): StyleResult => {
    const result = {
        pureStyle: {},
        configStyle: {},
    };

    if (!toolbarConfig || !compositeStyle) return result;

    Object.entries(toolbarConfig).forEach(([styleType, styleConfig]) => {
        const selectedStyle = styleConfig?.[compositeStyle?.[styleType]];
        if (selectedStyle) {
            Object.assign(
                result.pureStyle,
                selectedStyle.pure_style_mapping || {},
            );
            Object.assign(
                result.configStyle,
                selectedStyle.config_style_mapping || {},
            );
        }
    });

    return result;
};
